import React, { useState, useEffect } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from 'recharts';
import { zahlungenApi } from '../api';

const FARBEN = ['#6366f1', '#10b981', '#ef4444', '#3b82f6', '#f59e0b', '#8b5cf6'];
const TYP_FARBEN = { 'Sparvertrag': '#10b981', 'Kredit': '#ef4444', 'Versicherung': '#3b82f6', 'Abo': '#8b5cf6' };

export default function ZahlungenAuswertungPage() {
  const [daten, setDaten] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { laden(); }, []);

  async function laden() {
    setLoading(true);
    try { const res = await zahlungenApi.auswertung(); setDaten(res.data); }
    catch(e) { console.error(e); }
    setLoading(false);
  }

  if (loading) return <div style={{ padding: '40px', textAlign: 'center', color: '#94a3b8' }}>Lade Auswertung...</div>;
  if (!daten) return null;

  const typChartDaten = Object.entries(daten.nachTyp).map(([typ, d]) => ({
    typ, ausgaben: parseFloat(d.ausgaben.toFixed(2)), einzahlungen: parseFloat(d.einzahlungen.toFixed(2))
  }));

  const pieAusgaben = Object.entries(daten.nachTyp)
    .filter(([,d]) => d.ausgaben > 0)
    .map(([typ, d]) => ({ name: typ, value: parseFloat(d.ausgaben.toFixed(2)) }));

  return (
    <div style={{ padding: '24px', maxWidth: '1200px', margin: '0 auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <h1 style={{ margin: 0, fontSize: '28px' }}>📊 Zahlungen-Auswertung</h1>
        <span style={{ color: '#64748b', fontSize: '14px' }}>{daten.anzahlGesamt} aktive Zahlungen</span>
      </div>

      {/* KPI Karten */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '16px', marginBottom: '32px' }}>
        {[
          { label: 'Monatl. Ausgaben', wert: daten.monatlicheAusgaben, icon: '📤', farbe: '#ef4444', currency: true },
          { label: 'Monatl. Einzahlungen', wert: daten.monatlicheEinzahlungen, icon: '📥', farbe: '#10b981', currency: true },
          { label: 'Monatl. Saldo', wert: daten.monatlicheSaldo, icon: '⚖️', farbe: daten.monatlicheSaldo >= 0 ? '#10b981' : '#ef4444', currency: true },
          { label: 'Jährl. Ausgaben', wert: daten.jaehrlicheAusgaben, icon: '📅', farbe: '#f59e0b', currency: true },
          { label: 'Gesamtvermögen', wert: daten.gesamtWert, icon: '💼', farbe: '#6366f1', currency: true },
        ].map((k, i) => (
          <div key={i} style={{ background: 'white', borderRadius: '12px', padding: '20px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', borderLeft: `4px solid ${k.farbe}` }}>
            <div style={{ fontSize: '22px', marginBottom: '8px' }}>{k.icon}</div>
            <div style={{ fontSize: '20px', fontWeight: '700', color: k.farbe }}>
              {k.wert.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' })}
            </div>
            <div style={{ color: '#64748b', fontSize: '12px', marginTop: '4px' }}>{k.label}</div>
          </div>
        ))}
      </div>

      {/* Charts */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px', marginBottom: '32px' }}>
        <div style={{ background: 'white', borderRadius: '12px', padding: '20px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
          <h3 style={{ margin: '0 0 16px', color: '#1e293b' }}>📊 Ausgaben vs. Einzahlungen nach Typ</h3>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={typChartDaten}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="typ" tick={{ fontSize: 11 }} />
              <YAxis tickFormatter={v => v + '€'} />
              <Tooltip formatter={v => v.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' })} />
              <Bar dataKey="ausgaben" fill="#ef4444" radius={[4,4,0,0]} name="Ausgaben" />
              <Bar dataKey="einzahlungen" fill="#10b981" radius={[4,4,0,0]} name="Einzahlungen" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div style={{ background: 'white', borderRadius: '12px', padding: '20px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
          <h3 style={{ margin: '0 0 16px', color: '#1e293b' }}>🥧 Ausgaben-Verteilung</h3>
          {pieAusgaben.length === 0 ? <div style={{ textAlign: 'center', padding: '40px', color: '#94a3b8' }}>Keine Ausgaben</div> : (
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie data={pieAusgaben} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80}
                  label={({ name, percent }) => `${name} ${(percent*100).toFixed(0)}%`}>
                  {pieAusgaben.map((e, i) => <Cell key={i} fill={TYP_FARBEN[e.name] || FARBEN[i % FARBEN.length]} />)}
                </Pie>
                <Tooltip formatter={v => v.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' })} />
              </PieChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      {/* Tabelle nach Typ */}
      <div style={{ background: 'white', borderRadius: '12px', padding: '20px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', marginBottom: '24px' }}>
        <h3 style={{ margin: '0 0 16px', color: '#1e293b' }}>📋 Übersicht nach Typ</h3>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead><tr style={{ background: '#f8fafc' }}>
            {['Typ','Anzahl','Monatl. Ausgaben','Monatl. Einzahlungen','Saldo'].map(h =>
              <th key={h} style={{ padding: '10px 14px', textAlign: 'left', fontSize: '13px', color: '#64748b', fontWeight: '600' }}>{h}</th>)}
          </tr></thead>
          <tbody>
            {Object.entries(daten.nachTyp).map(([typ, d]) => {
              const saldo = d.einzahlungen - d.ausgaben;
              return (
                <tr key={typ} style={{ borderTop: '1px solid #f1f5f9' }}>
                  <td style={{ padding: '10px 14px', fontWeight: '600' }}>
                    <span style={{ background: (TYP_FARBEN[typ]||'#6366f1') + '20', color: TYP_FARBEN[typ]||'#6366f1', padding: '2px 10px', borderRadius: '99px' }}>{typ}</span>
                  </td>
                  <td style={{ padding: '10px 14px', color: '#64748b' }}>{d.anzahl}</td>
                  <td style={{ padding: '10px 14px', color: '#ef4444', fontWeight: '600' }}>{d.ausgaben > 0 ? d.ausgaben.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' }) : '–'}</td>
                  <td style={{ padding: '10px 14px', color: '#10b981', fontWeight: '600' }}>{d.einzahlungen > 0 ? d.einzahlungen.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' }) : '–'}</td>
                  <td style={{ padding: '10px 14px', fontWeight: '700', color: saldo >= 0 ? '#10b981' : '#ef4444' }}>{saldo.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' })}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Bald endende Zahlungen */}
      {daten.baldEndend.length > 0 && (
        <div style={{ background: 'white', borderRadius: '12px', padding: '20px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
          <h3 style={{ margin: '0 0 16px', color: '#1e293b' }}>⏳ Bald auslaufend (90 Tage)</h3>
          {daten.baldEndend.map(z => (
            <div key={z.id} style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', borderTop: '1px solid #f1f5f9' }}>
              <span style={{ fontWeight: '600' }}>{z.name}</span>
              <span style={{ color: '#ef4444', fontWeight: '600' }}>bis {new Date(z.ende).toLocaleDateString('de-DE')}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
